import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { getFileTypeIconClass } from '@iupics-components/standard/preview-doc/preview-doc.component';
import { DocumentPreviewComponent } from './document-preview.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuditFormat } from '../audit-window-ui/audit-format';
import { formatSize, splitName, tagName } from './documents-query';
import { DocAccess, DocDetail, DocItem, DocTag, DocVersion } from './documents.models';
import { DocumentsService } from './documents.service';
import { viewRefresher } from './view-refresh';

/**
 * Detail panel of a document: preview (through ws), actions, information, linked record, user tags (editable),
 * extracted text with the passages matching the search, technical metadata.
 */
/** a row of the tag editor */
interface TagRow {
  id: number;
  name: string;
  label: string;
  value: string;
  original: string;
  editable: boolean;
  isNew: boolean;
  removed: boolean;
}

@Component({
  selector: 'iu-document-detail',
  standalone: true,
  imports: [TranslateModule, DocumentPreviewComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './document-detail.component.html',
  styleUrl: './document-detail.component.scss',
})
export class DocumentDetailComponent {
  /** row the panel was opened from: shown while the detail loads */
  readonly row = input<DocItem | null>(null);
  readonly detail = input<DocDetail | null>(null);
  readonly loading = input(false);
  readonly error = input<string | null>(null);
  readonly fmt = input.required<AuditFormat>();
  readonly locale = input<string | undefined>(undefined);
  readonly busy = input(false);
  readonly q = input('');
  /** read-write access: extraction can be run again */
  readonly canRetryOcr = input(false);

  readonly closed = output<void>();
  readonly retry = output<void>();
  readonly rename = output<string>();
  readonly saveTags = output<Record<string, string>>();
  readonly favourite = output<boolean>();
  readonly download = output<void>();
  readonly openTab = output<void>();
  readonly copyLink = output<void>();
  readonly trash = output<void>();
  readonly restore = output<void>();
  readonly purge = output<void>();
  readonly openRecord = output<DocItem>();
  readonly showRecord = output<DocItem>();
  readonly linkRecord = output<DocItem>();
  readonly retryOcr = output<void>();
  readonly uploadVersion = output<File>();
  readonly restoreVersion = output<number>();
  readonly downloadVersion = output<DocVersion>();
  /** a new version is being sent (progress %), null otherwise */
  readonly versionProgress = input<number | null>(null);

  readonly item = computed(() => this.detail()?.item ?? this.row());
  readonly icon = computed(() => getFileTypeIconClass(this.item()?.fileName ?? ''));
  /** tag names to suggest when adding a tag */
  readonly tagNames = input<DocTag[]>([]);
  /** search the documents having this tag value */
  readonly filterTag = output<{ key: string; value: string }>();

  readonly renaming = signal(false);
  readonly renameBase = signal('');
  /** tag editor rows: the tags of the document, added ones, removed ones */
  readonly rows = signal<TagRow[]>([]);
  readonly visibleRows = computed(() => this.rows().filter((r) => !r.removed));
  readonly dirty = computed(() => this.rows().some((r) => r.removed || r.isNew || r.value !== r.original));
  /** a name that cannot be used (message key), null when the rows can be saved */
  readonly rowError = computed(() => {
    const names = new Set<string>();
    for (const r of this.rows()) {
      if (r.removed || !r.editable) continue;
      const n = tagName(r.name);
      if (r.isNew && !r.name.trim() && !r.value.trim()) continue;
      if (!/^[A-Z0-9_]{1,60}$/.test(n)) return 'documents.tagEditor.badName';
      if (names.has(n)) return 'documents.tagEditor.duplicate';
      if (r.isNew && this.rows().some((o) => !o.isNew && !o.removed && o.name === n)) return 'documents.tagEditor.duplicate';
      names.add(n);
    }
    return null;
  });
  #rowSeq = 0;
  readonly showText = signal(false);
  readonly showTechnical = signal(true);
  readonly confirmPurge = signal(false);
  readonly confirmRestore = signal<number | null>(null);
  /** access log (who opened it): loaded when the section is opened */
  readonly showAccess = signal(false);
  readonly access = signal<DocAccess[] | null>(null);
  readonly accessState = signal<'idle' | 'loading' | 'error'>('idle');
  readonly #docs = inject(DocumentsService);
  readonly #refresh = viewRefresher();
  #accessSub: Subscription | undefined;

  size = (n: number | null) => formatSize(n, this.locale());

  constructor() {
    // a new document (or a saved one): drafts and modes start again
    effect(
      () => {
        const d = this.detail();
        this.rows.set((d?.tags ?? []).map((t) => this.#row(t)));
        this.confirmPurge.set(false);
        this.confirmRestore.set(null);
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        this.item()?.docId;
        this.renaming.set(false);
        this.showText.set(false);
        this.#accessSub?.unsubscribe();
        this.showAccess.set(false);
        this.access.set(null);
        this.accessState.set('idle');
      },
      { allowSignalWrites: true }
    );
    inject(DestroyRef).onDestroy(() => this.#accessSub?.unsubscribe());
  }

  toggleAccess(): void {
    const open = !this.showAccess();
    this.showAccess.set(open);
    if (open && !this.access()) this.loadAccess();
  }

  loadAccess(): void {
    const docId = this.item()?.docId;
    if (!docId) return;
    this.#accessSub?.unsubscribe();
    this.accessState.set('loading');
    this.#accessSub = this.#docs.access(docId).subscribe({
      next: (rows) => {
        if (this.item()?.docId !== docId) return;
        this.access.set(rows);
        this.accessState.set('idle');
        this.#refresh();
      },
      error: () => {
        this.accessState.set('error');
        this.#refresh();
      },
    });
  }

  /** "viewed", "downloaded (link)", "downloaded v2 (zip)" */
  accessLabel(a: DocAccess): string {
    const t = (k: string, p?: object) => this.#translate.instant(k, p);
    let s = t(`documents.access.${a.action}`);
    if (a.version > 0) s += ` v${a.version}`;
    if (a.channel === 'link' || a.channel === 'zip') s += ` (${t(`documents.access.channel.${a.channel}`)})`;
    return s;
  }

  readonly #translate = inject(TranslateService);

  /** translated type, else the code */
  typeLabel(type: string | null): string {
    const key = `documents.types.${type ?? 'OTHER'}`;
    const label = this.#translate.instant(key);
    return label === key ? (type ?? '') : label;
  }

  startRename(): void {
    const it = this.item();
    if (!it?.editable) return;
    this.renameBase.set(splitName(it.fileName).base);
    this.renaming.set(true);
    setTimeout(() => (document.querySelector('.doc-rename-input') as HTMLInputElement | null)?.select());
  }

  commitRename(): void {
    const it = this.item();
    const base = this.renameBase().trim();
    this.renaming.set(false);
    if (!it || !base) return;
    const name = base + splitName(it.fileName).ext;
    if (name !== it.fileName) this.rename.emit(name);
  }

  onRenameKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitRename();
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      this.renaming.set(false);
    }
  }

  #row(t: DocTag): TagRow {
    return { id: ++this.#rowSeq, name: t.key, label: t.label, value: t.value ?? '', original: t.value ?? '', editable: t.editable, isNew: false, removed: false };
  }

  rowLabel(r: TagRow): string {
    if (r.name === 'DESCRIPTION') return this.#translate.instant('documents.tags.description');
    if (r.name === 'KEYWORDS') return this.#translate.instant('documents.tags.keywords');
    return r.label || r.name;
  }

  setRow(id: number, change: Partial<TagRow>): void {
    this.rows.update((rows) => rows.map((r) => (r.id === id ? { ...r, ...change } : r)));
  }

  addRow(): void {
    this.rows.update((rows) => [
      ...rows,
      { id: ++this.#rowSeq, name: '', label: '', value: '', original: '', editable: true, isNew: true, removed: false },
    ]);
    setTimeout(() => (document.querySelector('.doc-tag-row:last-of-type .doc-tag-name') as HTMLInputElement | null)?.focus());
  }

  removeRow(id: number): void {
    this.rows.update((rows) => rows.flatMap((r) => (r.id !== id ? [r] : r.isNew ? [] : [{ ...r, removed: true }])));
  }

  /** a new row whose name cannot be used (marked for screen readers) */
  badRow(r: TagRow): boolean {
    if (!r.isNew || r.removed || (!r.name.trim() && !r.value.trim())) return false;
    const n = tagName(r.name);
    return !/^[A-Z0-9_]{1,60}$/.test(n) || this.rows().some((o) => o.id !== r.id && !o.removed && (o.isNew ? tagName(o.name) : o.name) === n);
  }

  /** tag editor keys: Enter in a name goes to its value, Escape drops the changes (instead of closing the panel) */
  onTagKey(event: KeyboardEvent, isName: boolean): void {
    if (event.key === 'Enter' && isName) {
      event.preventDefault();
      ((event.target as HTMLElement).parentElement?.querySelector('.doc-tag-value') as HTMLInputElement | null)?.focus();
    } else if (event.key === 'Escape' && this.dirty()) {
      event.stopPropagation();
      this.resetRows();
    }
  }

  resetRows(): void {
    this.rows.set((this.detail()?.tags ?? []).map((t) => this.#row(t)));
  }

  /** changed tags by name (the server adds the prefix); an empty value removes the tag */
  commitTags(): void {
    if (this.rowError()) return;
    const changed: Record<string, string> = {};
    for (const r of this.rows()) {
      if (!r.editable) continue;
      if (r.removed) changed[r.name] = '';
      else if (r.isNew) {
        if (r.name.trim() && r.value.trim()) changed[tagName(r.name)] = r.value.trim();
      } else if (r.value !== r.original) changed[r.name] = r.value.trim();
    }
    if (Object.keys(changed).length) this.saveTags.emit(changed);
  }

  /** technical value as read by a user */
  techValue(t: DocTag): string {
    const v = t.value ?? '';
    switch (t.key) {
      case 'UFA_FILE_SIZE':
        return this.size(Number(v));
      case 'UFA_CREATED_DATE':
      case 'UFA_UPDATED_DATE':
      case 'UFA_OCR_DATE':
        return this.fmt().dateTime(v) || v;
      case 'UFA_OCR_STATUS':
        return this.#translate.instant(`documents.ocr.${v}`);
      case 'TYPE':
        return this.typeLabel(v);
      default:
        return v;
    }
  }

  onVersionPicked(input: HTMLInputElement): void {
    const file = input.files?.[0];
    input.value = '';
    if (file) this.uploadVersion.emit(file);
  }

  askRestore(v: DocVersion): void {
    if (this.confirmRestore() !== v.version) {
      this.confirmRestore.set(v.version);
      return;
    }
    this.confirmRestore.set(null);
    this.restoreVersion.emit(v.version);
  }

  askPurge(): void {
    if (!this.confirmPurge()) {
      this.confirmPurge.set(true);
      return;
    }
    this.confirmPurge.set(false);
    this.purge.emit();
  }

  /** passage with the search words wrapped in <mark> (text escaped first) */
  highlight(passage: string): string {
    const esc = passage.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
    const words = this.q()
      .split(/\s+/)
      .map((w) => w.trim())
      .filter((w) => w.length > 1)
      .map((w) => w.replace(/[&<>"']/g, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!words.length) return esc;
    return esc.replace(new RegExp(`(${words.join('|')})`, 'gi'), '<mark>$1</mark>');
  }

  trackTag = (_: number, t: DocTag) => t.key;
}
